import React, { useCallback, useEffect, useState } from 'react';
import LoadMore from '../../hooks/LoadMore';
import TrueImpactLoading from '../TrueImpactLoading';
import GridTable, { GridTableProps } from './GridTable';
import { TableHeaderId } from './GridTableRow';
import { RequestState } from '../../auth/hooks/useRequestState';

interface TableWithLazyLoadingProps<T extends TableHeaderId, U>
  extends Omit<GridTableProps<T>, 'rows'> {
  filters: U;
  className?: string;
  resultCount: number;
  allowLoadMore?: boolean;
  requestState: RequestState<T[]>;
  observerOptions?: IntersectionObserverInit;
  onRowClick?: (e: React.MouseEvent<HTMLTableRowElement>, row: T) => unknown;
  loadMore: (pageNumber: number, filters: U) => unknown;
}

const defaultObserverOptions: IntersectionObserverInit = {
  rootMargin: '60px 0px',
};
const TableWithLazyLoading = <T extends TableHeaderId, U>({
  className,
  filters,
  resultCount,
  requestState,
  allowLoadMore = true,
  observerOptions,
  loadMore,
  onRowClick,
  ...rest
}: TableWithLazyLoadingProps<T, U>) => {
  const [rows, setRows] = useState<T[]>([]);
  const [shouldLoadMore, setShouldLoadMore] = useState<boolean>(allowLoadMore);
  const [pageNumber, setPageNumber] = useState<number>(1);

  useEffect(() => {
    loadMore(1, filters);
    setRows([]);
    setPageNumber(1);
    allowLoadMore && setShouldLoadMore(true);
  }, [filters, loadMore, setRows, setPageNumber, setShouldLoadMore]);

  const handleLoadMore = useCallback(() => {
    const newPage = pageNumber + 1;
    loadMore(newPage, filters);
    setPageNumber(newPage);
  }, [pageNumber, filters, loadMore, setPageNumber]);

  useEffect(() => {
    if (requestState.type === 'REQUEST_SUCCESS') {
      const length = requestState.data.length;
      allowLoadMore && setShouldLoadMore(length >= resultCount);
      if (length === 0) return;
      setRows(s => [...s, ...requestState.data]);
    }
  }, [setRows, setShouldLoadMore, requestState]);

  return (
    <div className={className}>
      {requestState.type !== 'REQUEST_INIT' && (
        <GridTable
          {...rest}
          rows={rows}
          onRowClick={onRowClick}
          requestType={requestState.type}
        />
      )}
      {shouldLoadMore && (
        <LoadMore
          loadMore={handleLoadMore}
          options={observerOptions || defaultObserverOptions}
        >
          <TrueImpactLoading />
        </LoadMore>
      )}
    </div>
  );
};

export default React.memo(TableWithLazyLoading) as typeof TableWithLazyLoading;
